import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { httpsCallable } from 'firebase/functions';

import { functions } from '../../firebase/config';
import { saveSettingsImmediately } from '../../redux/slices/settingsSlice';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import {
  isFestpreisVorab,
  ZUSATZ_ARTEN,
  ZUSATZ_VORSCHLAEGE,
  zusatzIdFromLabel
} from '../../utils/preismodell';

const VEHICLE_TYPE_LABELS = {
  1: 'PKW (4 Sitze)',
  2: 'Großraum (6 Sitze)',
  3: 'Kleinbus (7 Sitze)',
  4: 'Kleinbus (8 Sitze)',
};

const euro = (cents) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;
const toNumber = (value, fallback = 0) => {
  const n = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
};
const toInput = (value) => (value === undefined || value === null ? '' : String(value).replace('.', ','));

const emptyCustomEntry = { label: '', art: 'pauschal', eur: '' };

/**
 * Einstellungen → „Preise & Abrechnung" → Festpreis vor der Fahrt.
 *
 * Nur bei Unternehmensform Mietwagen sichtbar. Grund-, km- und Nachttarif,
 * Großraum-/Spezialzuschläge und Anfahrtspreis stehen weiterhin im Abschnitt
 * „Tarif" — hier nur, was der Festpreis zusätzlich braucht: Minutenpreis,
 * Mindestpreis, Mindestentgelt (§ 51a PBefG), Angebotsgültigkeit und der
 * Katalog der Zusatzgebühren, die Fahrer beim Abschluss per Knopf hinzufügen.
 *
 * Verbindlich rechnet ausschließlich die Cloud Function `quoteRide`; der
 * Vorschau-Rechner ruft sie im Vorschau-Modus auf (speichert kein Angebot).
 *
 * nextcab_mobile/docs/general/mietwagen_festpreis_modus_konzept.md §5/§6/§11
 */
const FestpreisVorabSettingsSection = ({ canManage }) => {
  const dispatch = useDispatch();
  const general = useSelector((state) => state.settings.global) || {};
  const isSaving = useSelector((state) => state.settings.isSaving);
  const active = isFestpreisVorab(general);
  const nachtAktiv = general.nachtTarifAktiv === true;

  const [form, setForm] = useState(null);
  const [katalog, setKatalog] = useState([]);
  const [customEntry, setCustomEntry] = useState(emptyCustomEntry);
  const [showMindestentgelt, setShowMindestentgelt] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Formular aus den gespeicherten Werten — erneut, wenn ein anderes Fenster speichert.
  useEffect(() => {
    setForm({
      tarifPreisProMinute: toInput(general.tarifPreisProMinute ?? 0),
      nachtTarifPreisProMinute: toInput(general.nachtTarifPreisProMinute ?? general.tarifPreisProMinute ?? 0),
      tarifMindestpreis: toInput(general.tarifMindestpreis ?? 0),
      festpreisAngebotGueltigMin: String(general.festpreisAngebotGueltigMin ?? 10),
      mindestentgeltGrundpreis: toInput(general.mindestentgeltGrundpreis ?? 0),
      mindestentgeltPreisProKm: toInput(general.mindestentgeltPreisProKm ?? 0),
      zusatzgebuehrenMaxProFahrtEur: toInput(general.zusatzgebuehrenMaxProFahrtEur ?? 15),
    });
    setKatalog(Array.isArray(general.zusatzgebuehren)
      ? general.zusatzgebuehren.map((e) => ({ ...e, eur: toInput(e.eur ?? 0), freiMinuten: String(e.freiMinuten ?? 5) }))
      : []);
    if (toNumber(general.mindestentgeltGrundpreis) > 0 || toNumber(general.mindestentgeltPreisProKm) > 0) {
      setShowMindestentgelt(true);
    }
  }, [
    general.tarifPreisProMinute, general.nachtTarifPreisProMinute, general.tarifMindestpreis,
    general.festpreisAngebotGueltigMin, general.mindestentgeltGrundpreis, general.mindestentgeltPreisProKm,
    general.zusatzgebuehrenMaxProFahrtEur, general.zusatzgebuehren
  ]);

  const grossraumEur = toNumber(general.tarifGrossraumzuschlag, 5);
  const katalogIds = useMemo(() => katalog.map((e) => e.id), [katalog]);
  const fehlendeVorschlaege = ZUSATZ_VORSCHLAEGE.filter((v) => !katalogIds.includes(v.id));

  if (!form) return null;

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));
  const updateEntry = (index, patch) => setKatalog((list) => list.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  const removeEntry = (index) => setKatalog((list) => list.filter((_, i) => i !== index));
  const addVorschlaege = () => setKatalog((list) => [
    ...list,
    ...fehlendeVorschlaege.map((v) => ({ ...v, eur: toInput(v.eur), freiMinuten: String(v.freiMinuten) })),
  ]);
  const addCustom = () => {
    const label = customEntry.label.trim();
    if (!label || toNumber(customEntry.eur) <= 0) return;
    setKatalog((list) => [...list, {
      id: zusatzIdFromLabel(label, list.map((e) => e.id)),
      label,
      art: customEntry.art,
      eur: customEntry.eur,
      freiMinuten: '5',
      aktiv: true,
    }]);
    setCustomEntry(emptyCustomEntry);
  };

  const validationError = (() => {
    if (toNumber(form.tarifPreisProMinute) < 0 || toNumber(form.nachtTarifPreisProMinute) < 0) return 'Der Minutenpreis darf nicht negativ sein.';
    const gueltig = Math.round(toNumber(form.festpreisAngebotGueltigMin, 10));
    if (gueltig < 2 || gueltig > 60) return 'Die Angebotsgültigkeit muss zwischen 2 und 60 Minuten liegen.';
    if (toNumber(form.zusatzgebuehrenMaxProFahrtEur) < 0) return 'Der Höchstbetrag darf nicht negativ sein.';
    for (const e of katalog) {
      if (!String(e.label || '').trim()) return 'Jede Zusatzgebühr braucht eine Bezeichnung.';
      if (e.art !== 'grossraum' && toNumber(e.eur) <= 0) return `„${e.label}": Betrag fehlt.`;
    }
    return null;
  })();

  const handleSave = async () => {
    if (validationError) return;
    try {
      await dispatch(saveSettingsImmediately({
        values: {
          tarifPreisProMinute: Math.max(0, toNumber(form.tarifPreisProMinute)),
          nachtTarifPreisProMinute: Math.max(0, toNumber(form.nachtTarifPreisProMinute)),
          tarifMindestpreis: Math.max(0, toNumber(form.tarifMindestpreis)),
          festpreisAngebotGueltigMin: Math.round(toNumber(form.festpreisAngebotGueltigMin, 10)),
          mindestentgeltGrundpreis: showMindestentgelt ? Math.max(0, toNumber(form.mindestentgeltGrundpreis)) : 0,
          mindestentgeltPreisProKm: showMindestentgelt ? Math.max(0, toNumber(form.mindestentgeltPreisProKm)) : 0,
          zusatzgebuehrenMaxProFahrtEur: Math.max(0, toNumber(form.zusatzgebuehrenMaxProFahrtEur, 15)),
          zusatzgebuehren: katalog.map((e) => ({
            id: e.id,
            label: String(e.label).trim(),
            art: e.art,
            eur: e.art === 'grossraum' ? 0 : Math.max(0, toNumber(e.eur)),
            freiMinuten: e.art === 'wartezeit' ? Math.max(0, Math.round(toNumber(e.freiMinuten, 5))) : 0,
            aktiv: e.aktiv !== false,
          })),
        },
      })).unwrap();
      setSnackbar({ open: true, message: 'Festpreis-Einstellungen gespeichert.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler beim Speichern: ${error}`, severity: 'error' });
    }
  };

  const disabled = !canManage;

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'surface.100' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          <LockIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Festpreis vor der Fahrt
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Mietwagen: Der Fahrgast sieht vor der Bestellung einen verbindlichen Preis
        </Typography>
      </Box>

      {!active ? (
        <Box sx={{ px: 3, py: 2, bgcolor: 'surface.50' }}>
          <Alert severity="info">
            Ausgeschaltet — Fahrer geben den Endpreis nach der Fahrt ein (z.&nbsp;B. mit
            Wegstreckenzähler). Umstellen unter <strong>Betrieb &amp; Disposition → Unternehmensform</strong>.
          </Alert>
        </Box>
      ) : (
        <Box sx={{ px: 3, py: 2, bgcolor: 'surface.50' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Formel:</strong> Grundpreis + km &times; Preis/km + Minuten &times; Preis/Minute
            (+ Großraum- oder Spezialzuschlag, + Anfahrt), mindestens der Mindestpreis, gerundet auf 10&nbsp;Cent.
            Grundpreis, Kilometerpreis, Nachttarif und Zuschläge stehen oben unter „Tarif".
            Ein passender Eintrag in der Festpreisliste hat Vorrang. Für Tag oder Nacht zählt die Abholzeit.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Preis pro Minute" value={form.tarifPreisProMinute}
                onChange={set('tarifPreisProMinute')} disabled={disabled}
                InputProps={{ endAdornment: <InputAdornment position="end">€/min</InputAdornment> }}
                helperText="Fahrzeit laut Routenplaner mit Verkehr. 0 = nur Strecke"
              />
            </Grid>
            {nachtAktiv && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small" label="Preis pro Minute (Nacht)" value={form.nachtTarifPreisProMinute}
                  onChange={set('nachtTarifPreisProMinute')} disabled={disabled}
                  InputProps={{ endAdornment: <InputAdornment position="end">€/min</InputAdornment> }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Mindestpreis" value={form.tarifMindestpreis}
                onChange={set('tarifMindestpreis')} disabled={disabled}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                helperText="0 = kein Mindestpreis"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Angebot gültig" value={form.festpreisAngebotGueltigMin}
                onChange={set('festpreisAngebotGueltigMin')} disabled={disabled}
                InputProps={{ endAdornment: <InputAdornment position="end">Minuten</InputAdornment> }}
                helperText="Danach berechnet die App den Preis neu"
              />
            </Grid>
          </Grid>

          <Button
            size="small" sx={{ mt: 2 }}
            onClick={() => setShowMindestentgelt((v) => !v)}
            endIcon={showMindestentgelt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            Mindestentgelt der Behörde (§ 51a PBefG)
          </Button>
          <Collapse in={showMindestentgelt}>
            <Alert severity="warning" sx={{ my: 1 }}>
              Nur ausfüllen, wenn Ihre Genehmigungsbehörde ein Mindestbeförderungsentgelt für Mietwagen
              festgesetzt hat. Der Festpreis unterschreitet diesen Betrag dann nie, auch nicht bei Einträgen der
              Festpreisliste.
            </Alert>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small" label="Grundbetrag" value={form.mindestentgeltGrundpreis}
                  onChange={set('mindestentgeltGrundpreis')} disabled={disabled}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small" label="je Kilometer" value={form.mindestentgeltPreisProKm}
                  onChange={set('mindestentgeltPreisProKm')} disabled={disabled}
                  InputProps={{ endAdornment: <InputAdornment position="end">€/km</InputAdornment> }}
                />
              </Grid>
            </Grid>
          </Collapse>

          {/* Zusatzgebühren-Katalog (Konzept §6) */}
          <Typography variant="subtitle2" sx={{ mt: 3 }}>Zusatzgebühren</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Fahrer können beim Abschluss nur diese Gebühren per Knopf hinzufügen, freie Beträge gibt es nicht.
            Fahrgäste sehen die aktiven Gebühren schon vor der Bestellung.
          </Typography>
          {katalog.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bezeichnung</TableCell>
                  <TableCell>Art</TableCell>
                  <TableCell>Betrag</TableCell>
                  <TableCell>Freiminuten</TableCell>
                  <TableCell>Aktiv</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {katalog.map((entry, index) => (
                  <TableRow key={entry.id}>
                    <TableCell sx={{ minWidth: 200 }}>
                      <TextField
                        size="small" fullWidth value={entry.label} disabled={disabled}
                        onChange={(e) => updateEntry(index, { label: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      {ZUSATZ_ARTEN.find((a) => a.value === entry.art)?.label || entry.art}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {entry.art === 'grossraum' ? (
                        <Tooltip title="Betrag aus dem Tarif (Großraumzuschlag)">
                          <Typography variant="body2">{euro(Math.round(grossraumEur * 100))}</Typography>
                        </Tooltip>
                      ) : (
                        <TextField
                          size="small" value={entry.eur} disabled={disabled}
                          onChange={(e) => updateEntry(index, { eur: e.target.value })}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {entry.art === 'wartezeit' ? '€/min' : entry.art === 'pro_stueck' ? '€/Stück' : '€'}
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      {entry.art === 'wartezeit' ? (
                        <TextField
                          size="small" value={entry.freiMinuten} disabled={disabled}
                          onChange={(e) => updateEntry(index, { freiMinuten: e.target.value })}
                          InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={entry.aktiv !== false} disabled={disabled}
                        onChange={(e) => updateEntry(index, { aktiv: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeEntry(index)} disabled={disabled} aria-label="Entfernen">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {katalog.some((e) => e.art === 'grossraum') && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              Der Großraumzuschlag wird nur angeboten, wenn die Fahrt ohne Großraum gebucht wurde und der Fahrer
              tatsächlich einen Großraumwagen fährt. Wartezeit zählt erst ab der vereinbarten Abholzeit.
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2 }}>
            {fehlendeVorschlaege.length > 0 && (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addVorschlaege} disabled={disabled}>
                Vorschläge übernehmen ({fehlendeVorschlaege.map((v) => v.label.split(' (')[0]).join(', ')})
              </Button>
            )}
          </Box>
          <Grid container spacing={1} sx={{ mt: 1 }} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                size="small" fullWidth label="Eigene Gebühr" value={customEntry.label} disabled={disabled}
                onChange={(e) => setCustomEntry((c) => ({ ...c, label: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select size="small" fullWidth label="Art" value={customEntry.art} disabled={disabled}
                onChange={(e) => setCustomEntry((c) => ({ ...c, art: e.target.value }))}
              >
                <MenuItem value="pauschal">Pauschal</MenuItem>
                <MenuItem value="pro_stueck">Je Stück</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                size="small" fullWidth label="Betrag" value={customEntry.eur} disabled={disabled}
                onChange={(e) => setCustomEntry((c) => ({ ...c, eur: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth variant="outlined" startIcon={<AddIcon />} onClick={addCustom}
                disabled={disabled || !customEntry.label.trim() || toNumber(customEntry.eur) <= 0}
              >
                Hinzufügen
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Höchstbetrag pro Fahrt" value={form.zusatzgebuehrenMaxProFahrtEur}
                onChange={set('zusatzgebuehrenMaxProFahrtEur')} disabled={disabled}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                helperText="Summe aller Zusatzgebühren einer Fahrt. Bei PayPal wird dieser Betrag zusätzlich reserviert"
              />
            </Grid>
          </Grid>

          {validationError && <Alert severity="error" sx={{ mt: 2 }}>{validationError}</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained" color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={disabled || isSaving || Boolean(validationError)}
            >
              {isSaving ? 'Wird gespeichert...' : 'Festpreis-Einstellungen speichern'}
            </Button>
          </Box>

          <FestpreisVorschau />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

/**
 * Vorschau-Rechner: echte Strecke → Festpreis je Fahrzeugtyp mit
 * Aufschlüsselung. Rechnet mit den GESPEICHERTEN Werten (Cloud Function).
 */
const FestpreisVorschau = () => {
  const [pickup, setPickup] = useState({ address: '', location: null });
  const [destination, setDestination] = useState({ address: '', location: null });
  const [abholzeit, setAbholzeit] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canCalculate = pickup.location && destination.location && !loading;

  const calculate = async () => {
    setLoading(true);
    setError(null);
    try {
      const scheduledTimeMs = abholzeit ? new Date(abholzeit).getTime() : null;
      const response = await httpsCallable(functions, 'quoteRide')({
        preview: true,
        pickup: { lat: pickup.location.latitude, lng: pickup.location.longitude, address: pickup.address },
        destination: { lat: destination.location.latitude, lng: destination.location.longitude, address: destination.address },
        scheduledTimeMs,
      });
      setResult(response.data);
    } catch (e) {
      setResult(null);
      setError(e?.message || 'Berechnung fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const fieldFor = (state, setState, label, role) => (
    <PlaceAutocomplete
      label={label}
      subDestinationRole={role}
      requireLocation
      location={state.location}
      value={state.address}
      onChange={(e) => {
        const address = e.target.value;
        setState((s) => ({ address, location: address === s.address ? s.location : null }));
      }}
      onPlaceSelect={(address, location) => setState({ address, location })}
      size="small"
      fullWidth
    />
  );

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          <CalculateIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
          Vorschau-Rechner
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rechnet wie die App mit den gespeicherten Werten — Änderungen oben vorher speichern.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>{fieldFor(pickup, setPickup, 'Abholort', 'pickup')}</Grid>
          <Grid item xs={12} md={5}>{fieldFor(destination, setDestination, 'Ziel', 'destination')}</Grid>
          <Grid item xs={12} md={2}>
            <TextField
              size="small" fullWidth type="datetime-local" label="Abholzeit" value={abholzeit}
              onChange={(e) => setAbholzeit(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="leer = jetzt"
            />
          </Grid>
        </Grid>
        <Button
          sx={{ mt: 2 }} variant="outlined" onClick={calculate} disabled={!canCalculate}
          startIcon={loading ? <CircularProgress size={18} /> : <CalculateIcon />}
        >
          Festpreis berechnen
        </Button>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {String(result.distanceKm).replace('.', ',')} km · {result.durationMin} min ·{' '}
              {result.night ? 'Nachttarif' : 'Tagtarif'}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fahrzeug</TableCell>
                  <TableCell align="right">Festpreis</TableCell>
                  <TableCell>Zusammensetzung</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(result.preise || {}).map(([vt, q]) => (
                  <TableRow key={vt}>
                    <TableCell>{VEHICLE_TYPE_LABELS[vt] || `Typ ${vt}`}</TableCell>
                    <TableCell align="right"><strong>{euro(q.cents)}</strong></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {q.quelle === 'festpreisliste'
                          ? `Festpreisliste (${q.festpreisLabel || ''}) ${euro(q.basisCents)}`
                          : `Tarif ${euro(q.basisCents)}`}
                        {q.zuschlagCents > 0 && ` + Zuschlag ${euro(q.zuschlagCents)}`}
                        {q.anfahrtCents > 0 && ` + Anfahrt ${euro(q.anfahrtCents)}`}
                        {q.untergrenze === 'mindestpreis' && ' · Mindestpreis greift'}
                        {q.untergrenze === 'mindestentgelt' && ' · Mindestentgelt greift'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </APIProvider>
  );
};

export default FestpreisVorabSettingsSection;
